// Implements: F-UIX-019
/**
 * The four tiles that lead the totals page (UX-11, the mockup's `.tiles`): the cost, the
 * generations, the error rate and the tokens of the overall total of the range, each a named group
 * with its figure and one line of context. Organization level only, read from the same answer as
 * the table: no extra request. The mockup's "Organizaciones en tope" tile needs every
 * organization's ceiling (one read each, no joined endpoint) and is left out (backend gap).
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { RowFigures } from "../model/platformRows";
import { rateText } from "../model/status";
import styles from "./PlatformTotals.module.css";
import { costsText, countText } from "./TotalsRow";

interface TileProps {
  readonly label: string;
  readonly value: string;
  readonly context: string;
}

function Tile({ label, value, context }: TileProps): ReactElement {
  const labelId = useId();
  return (
    <div className={styles.tile} role="group" aria-labelledby={labelId}>
      <p id={labelId} className={styles.tileLabel}>
        {label}
      </p>
      <p className={styles.tileValue}>{value}</p>
      <p className={styles.tileContext}>{context}</p>
    </div>
  );
}

export interface TotalsTilesProps {
  readonly total: RowFigures;
  /** How many organizations the range has a row for. */
  readonly organizations: number;
}

export function TotalsTiles({ total, organizations }: TotalsTilesProps): ReactElement {
  const { t } = useTranslation();
  return (
    <section className={styles.tiles} aria-label={t("admin.totals.tiles")}>
      <Tile
        label={t("admin.totals.tile.cost")}
        value={costsText(total.costs, t("admin.totals.noCost"))}
        context={t("admin.totals.tile.organizations", { count: countText(organizations) })}
      />
      <Tile
        label={t("admin.totals.tile.generations")}
        value={countText(total.generations)}
        context={t("admin.totals.tile.delivered", { count: countText(total.delivered) })}
      />
      <Tile
        label={t("admin.totals.tile.errorRate")}
        value={rateText(total.generations === 0 ? 0 : total.failed / total.generations)}
        context={t("admin.totals.tile.failures", { failed: countText(total.failed), cancelled: countText(total.cancelled) })}
      />
      <Tile
        label={t("admin.totals.tile.tokens")}
        value={countText(total.promptTokens + total.completionTokens)}
        context={t("admin.totals.tile.tokenSplit", {
          prompt: countText(total.promptTokens),
          completion: countText(total.completionTokens),
        })}
      />
    </section>
  );
}
